import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('Fname', (form.elements.namedItem('Fname') as HTMLInputElement).value);
    data.append('Fcontact_number', (form.elements.namedItem('Fcontact_number') as HTMLInputElement).value);
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('Fmessage', (form.elements.namedItem('Fmessage') as HTMLTextAreaElement).value);

    try {
      const res = await fetch('https://readdy.ai/api/form/d71o64c991ove783t3d0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-white">
      <div className="flex flex-col md:flex-row">
        {/* Left: Image with Contact overlay */}
        <div className="w-full md:w-1/2 relative min-h-[240px] sm:min-h-[340px] md:min-h-[480px]">
          <img
            src="https://cdn1.npcdn.net/userfiles/25709/image/img2-beige(1).png"
            alt="Contact"
            className="w-full h-full object-cover object-top absolute inset-0"
          />
          <div className="absolute bottom-6 sm:bottom-8 left-6 sm:left-8">
            <span
              className="text-3xl sm:text-5xl font-bold text-white drop-shadow-lg"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Contact
            </span>
          </div>
        </div>

        {/* Right: Map + Form + Info */}
        <div className="w-full md:w-1/2">
          {/* Google Map */}
          <div className="w-full h-[200px] sm:h-[260px]">
            <iframe
              src="https://maps.google.com/maps?hl=en&q=SFERA+RESIDENCY,+JALAN+ATMOSPHERE+1,+BANDAR+PUTRA+PERMAI,+43300+SERI+KEMBANGAN,+SELANGOR&ie=UTF8&iwloc=B&output=embed"
              width="100%"
              height="100%"
              frameBorder={0}
              scrolling="no"
              title="Location"
            />
          </div>

          <div className="p-5 sm:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center mb-5 sm:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Contact Us
            </h1>

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Form */}
              <div className="flex-1">
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                  <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wide text-gray-600 mb-1">
                      Name<span className="text-red-500">*</span>
                    </label>
                    <input
                      name="Fname"
                      required
                      placeholder="Enter name here"
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="flex-1">
                      <label className="block text-xs uppercase tracking-wide text-gray-600 mb-1">
                        Contact Number<span className="text-red-500">*</span>
                      </label>
                      <input
                        name="Fcontact_number"
                        required
                        placeholder="Enter contact number"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs uppercase tracking-wide text-gray-600 mb-1">
                        Email<span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="Enter email address"
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs uppercase tracking-wide text-gray-600 mb-1">
                      Message (optional)
                    </label>
                    <textarea
                      name="Fmessage"
                      placeholder="Enter message here"
                      maxLength={500}
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500 resize-none"
                    />
                  </div>

                  {status === 'success' && (
                    <div className="mb-3 text-green-600 text-sm bg-green-50 rounded p-2">
                      Message sent successfully! We will contact you soon.
                    </div>
                  )}
                  {status === 'error' && (
                    <div className="mb-3 text-red-600 text-sm bg-red-50 rounded p-2">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="bg-gray-800 text-white px-6 py-2 text-sm hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact info */}
              <div className="lg:w-44 text-sm text-gray-600">
                <p className="font-semibold text-gray-800 mb-1">THIRD STUDIO</p>
                <p className="text-xs leading-relaxed mb-3">
                  UNIT C-LG-07,<br />
                  SFERA RESIDENCY,<br />
                  JALAN ATMOSPHERE 1,<br />
                  BANDAR PUTRA PERMAI,<br />
                  43300 SERI KEMBANGAN,<br />
                  SELANGOR.
                </p>

                {/* Navigation buttons */}
                <div className="flex items-center gap-2 mb-4">
                  <a
                    href="https://waze.com/ul/hw282crj1u"
                    target="_blank"
                    rel="nofollow noreferrer"
                    title="Navigate with Waze"
                    className="flex items-center gap-1.5 bg-[#05c8f7] hover:bg-[#00b8e0] active:scale-95 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <img
                      src="https://cdn1.npcdn.net/img/1548296535y_waze.png"
                      alt="Waze"
                      className="h-4 w-4 object-contain"
                    />
                    Waze
                  </a>
                  <a
                    href="https://share.google/RmT2JJsHzC7dbZJJl"
                    target="_blank"
                    rel="nofollow noreferrer"
                    title="Navigate with Google Maps"
                    className="flex items-center gap-1.5 bg-white hover:bg-gray-50 active:scale-95 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <img
                      src="https://cdn1.npcdn.net/img/1548298407y_googlemap.png"
                      alt="Google Maps"
                      className="h-4 w-4 object-contain"
                    />
                    Google Maps
                  </a>
                </div>

                <p className="font-semibold text-gray-800 text-xs mb-1">Contact No</p>
                <a href="tel:+60174866108" className="text-sm text-gray-600 hover:text-gray-800 block mb-4">
                  +6017-4866108
                </a>
                <a
                  href="https://wa.me/60125618333?text=Hi, i am interested to know more."
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center gap-2 bg-green-500 text-white px-3 py-2 rounded text-xs hover:bg-green-600 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-base" />
                  <div>
                    <p className="font-semibold whitespace-nowrap">WhatsApp Us</p>
                    <span>Click to chat</span>
                  </div>
                </a>
                <div className="flex gap-2 mt-4">
                  <a href="https://www.facebook.com/insglobalmy/" target="_blank" rel="nofollow noreferrer"
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-sm text-gray-700" />
                  </a>
                  <a href="https://www.instagram.com/insglobalmy_official/" target="_blank" rel="nofollow noreferrer"
                    className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-sm text-gray-700" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
